import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Upload, Check, ArrowRight, Star, Heart, Mail, Loader2, Clock, CheckCircle, XCircle, Eye, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// Header Component
const Header = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="header" data-testid="header">
      <div className="header-content">
        <div className="logo" onClick={() => navigate("/")} data-testid="logo">
          <img src="https://customer-assets.emergentagent.com/job_thread-designs-2/artifacts/f4l304li_logoasdas.png" alt="String Creations" className="logo-image" />
        </div>
        <nav className="nav-links" data-testid="nav-links">
          <a href="/#how-it-works">How It Works</a>
          <a href="/#gallery">Gallery</a>
          <a href="/#pricing">Pricing</a>
          <a href="/about" className="nav-link">About</a>
          <a href="/check-order" className="nav-link">Check Order</a>
        </nav>
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <div className="header-buttons">
          <Button 
            onClick={() => navigate("/check-order")} 
            variant="outline"
            className="check-order-btn-mobile"
            data-testid="check-order-mobile-btn"
          >
            Check Order
          </Button>
          <Button 
            onClick={() => navigate("/create-order")} 
            className="cta-btn"
            data-testid="create-order-nav-btn"
          >
            Create Your Art
          </Button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <a 
            href="/create-order" 
            onClick={() => setMobileMenuOpen(false)}
            className="mobile-order-now-btn"
            data-testid="mobile-order-now-btn"
          >
            Order Now
          </a>
          <a href="/#how-it-works" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
          <a href="/#gallery" onClick={() => setMobileMenuOpen(false)}>Gallery</a>
          <a href="/#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
          <a href="/about" onClick={() => setMobileMenuOpen(false)}>About</a>
          <a href="/check-order" onClick={() => setMobileMenuOpen(false)}>Check Order</a>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const navigate = useNavigate();
  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-content">
        <div className="hero-text">
          <span className="hero-badge">Handcrafted with Love</span>
          <h1 className="hero-title">
            Transform Your <span className="gradient-text">Portrait</span> Into Stunning String Art
          </h1>
          <p className="hero-description">
            Each piece is meticulously handcrafted by skilled artisans, transforming your cherished photos into unique thread masterpieces that capture every detail.
          </p>
          <div className="hero-buttons">
            <Button 
              onClick={() => navigate("/create-order")} 
              className="primary-btn"
              data-testid="hero-cta-btn"
            >
              Start Creating <ArrowRight size={18} />
            </Button>
            <Button 
              variant="outline" 
              className="secondary-btn"
              onClick={() => document.getElementById('gallery').scrollIntoView({ behavior: 'smooth' })}
              data-testid="view-gallery-btn"
            >
              View Gallery
            </Button>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">Happy Customers</span>
            </div>
            <div className="stat">
              <span className="stat-number">4.9</span>
              <span className="stat-label">Star Rating</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Handmade</span>
            </div>
          </div>
        </div>
        <div className="hero-image">
          <div className="image-frame">
            <img 
              src="https://customer-assets.emergentagent.com/job_thread-designs-2/artifacts/eougcz8r_hero-string-art.jpg" 
              alt="Lion string art portrait"
            />
          </div>
          <div className="floating-card card-1">
            <Star fill="#f59e0b" color="#f59e0b" size={16} />
            <span>Premium Quality</span>
          </div>
          <div className="floating-card card-2">
            <Heart fill="#ef4444" color="#ef4444" size={16} />
            <span>Made with Love</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Upload Your Photo",
      description: "Choose your favorite portrait or photo that you'd like transformed into string art."
    },
    {
      number: "02",
      title: "Select Your Size",
      description: "Pick from our three carefully crafted sizes to match your space perfectly."
    },
    {
      number: "03",
      title: "We Create Magic",
      description: "Our artisans meticulously handcraft your unique piece using premium threads."
    },
    {
      number: "04",
      title: "Delivered to You",
      description: "Receive your masterpiece, carefully packaged and ready to display."
    }
  ];

  return (
    <section id="how-it-works" className="how-it-works-section" data-testid="how-it-works-section">
      <div className="section-header">
        <span className="section-badge">The Process</span>
        <h2 className="section-title">How It Works</h2>
        <p className="section-description">Four simple steps to your personalized string art masterpiece</p>
      </div>
      <div className="steps-grid">
        {steps.map((step, index) => (
          <div key={index} className="step-card" data-testid={`step-${index + 1}`}>
            <span className="step-number">{step.number}</span>
            <h3 className="step-title">{step.title}</h3>
            <p className="step-description">{step.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const images = [
    { src: "/gallery/ai_portrait_1.png", alt: "Woman portrait string art" },
    { src: "https://customer-assets.emergentagent.com/job_thread-designs-2/artifacts/1filzzhu_lion.png", alt: "Lion portrait string art" },
    { src: "https://customer-assets.emergentagent.com/job_thread-designs-2/artifacts/t3tstuwl_download%20%281%29.png", alt: "Lady with cat string art" },
    { src: "https://customer-assets.emergentagent.com/job_thread-designs-2/artifacts/pwkwivmg_hehehe.png", alt: "Man portrait string art" },
    { src: "https://customer-assets.emergentagent.com/job_thread-designs-2/artifacts/skb615kv_download.png", alt: "Baby portrait string art" },
    { src: "/gallery/ai_portrait_5.png", alt: "Couple portrait string art" }
  ];

  return (
    <section id="gallery" className="gallery-section" data-testid="gallery-section">
      <div className="section-header">
        <span className="section-badge">Our Work</span>
        <h2 className="section-title">Gallery of Creations</h2>
        <p className="section-description">Each piece tells a unique story, handcrafted with passion</p>
      </div>
      <div className="gallery-grid">
        {images.map((img, index) => (
          <div key={index} className="gallery-item" data-testid={`gallery-item-${index + 1}`}>
            <img src={img.src} alt={img.alt} />
            <div className="gallery-overlay">
              <Heart size={24} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Placement Tips Section
const PlacementTipsSection = () => {
  const placementTips = [
    {
      image: "https://customer-assets.emergentagent.com/job_6173121a-caa3-4291-ab6e-0180ed605ae5/artifacts/pzvx9e2s_1.png",
      alt: "Living Room"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_6173121a-caa3-4291-ab6e-0180ed605ae5/artifacts/p7000nw7_2.png",
      alt: "Stairway"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_6173121a-caa3-4291-ab6e-0180ed605ae5/artifacts/678vszgr_3.png",
      alt: "Bedroom"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_6173121a-caa3-4291-ab6e-0180ed605ae5/artifacts/4rmohs6i_4.png",
      alt: "Entryway"
    }
  ];

  return (
    <section className="placement-tips-section" data-testid="placement-tips-section">
      <div className="section-header">
        <span className="section-badge">Placement Guide</span>
        <h2 className="section-title">Where to Display Your Art</h2>
        <p className="section-description">Expert tips for showcasing your string art in any space</p>
      </div>
      <div className="placement-tips-grid">
        {placementTips.map((tip, index) => (
          <div key={index} className="placement-tip-card" data-testid={`placement-tip-${index + 1}`}>
            <img src={tip.image} alt={tip.alt} />
          </div>
        ))}
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const navigate = useNavigate();
  const sizes = [
    { key: "small", name: "Small", dimensions: "36x36cm", price: 169, popular: false },
    { key: "medium", name: "Medium", dimensions: "53x53cm", price: 249, popular: true },
    { key: "large", name: "Large", dimensions: "73x73cm", price: 359, popular: false }
  ];

  return (
    <section id="pricing" className="pricing-section" data-testid="pricing-section">
      <div className="section-header">
        <span className="section-badge">Pricing</span>
        <h2 className="section-title">Choose Your Size</h2>
        <p className="section-description">Artisanal quality at every size</p>
        <div className="shipping-badge">
          <span>🌍 Free Worldwide Shipping • Delivery: 3-5 weeks</span>
        </div>
      </div>
      <div className="pricing-grid">
        {sizes.map((size) => (
          <Card key={size.key} className={`pricing-card ${size.popular ? 'popular' : ''}`} data-testid={`pricing-${size.key}`}>
            {size.popular && <span className="popular-badge">Most Popular</span>}
            <CardHeader>
              <CardTitle className="pricing-name">{size.name}</CardTitle>
              <CardDescription className="pricing-dimensions">{size.dimensions}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="pricing-price">
                <span className="currency">$</span>
                <span className="amount">{size.price}</span>
              </div>
              <ul className="pricing-features">
                <li><Check size={16} /> Premium wooden board</li>
                <li><Check size={16} /> High-quality threads</li>
                <li><Check size={16} /> Handcrafted with care</li>
                <li><Check size={16} /> Ready to hang</li>
                <li><Check size={16} /> Free shipping</li>
              </ul>
              <Button 
                onClick={() => navigate("/create-order", { state: { selectedSize: size.key } })} 
                className={`pricing-btn ${size.popular ? 'popular-btn' : ''}`}
                data-testid={`select-${size.key}-btn`}
              >
                Select {size.name}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      text: "Absolutely stunning! The detail in my portrait string art exceeded all expectations. A true masterpiece!",
      rating: 5
    },
    {
      name: "Michael Chen",
      text: "Gave this as a gift to my parents for their anniversary. They were moved to tears. Incredible craftsmanship.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      text: "The quality is outstanding. It's become the centerpiece of my living room. Worth every penny!",
      rating: 5
    }
  ];

  return (
    <section className="testimonials-section" data-testid="testimonials-section">
      <div className="section-header">
        <span className="section-badge">Reviews</span>
        <h2 className="section-title">What Our Customers Say</h2>
      </div>
      <div className="testimonials-grid">
        {testimonials.map((testimonial, index) => (
          <Card key={index} className="testimonial-card" data-testid={`testimonial-${index + 1}`}>
            <CardContent>
              <div className="stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} fill="#f59e0b" color="#f59e0b" size={18} />
                ))}
              </div>
              <p className="testimonial-text">"{testimonial.text}"</p>
              <p className="testimonial-author">— {testimonial.name}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div className="contact-content">
        <div className="contact-info">
          <span className="section-badge">Get in Touch</span>
          <h2 className="section-title">Contact Us</h2>
          <p className="contact-description">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
          <div className="contact-details">
            <div className="contact-item">
              <Mail size={20} />
              <span>fkasuli@gmail.com</span>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
          <div className="form-group">
            <Label htmlFor="name">Name</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              data-testid="contact-name-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              type="email" 
              value={formData.email} 
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              data-testid="contact-email-input"
            />
          </div>
          <div className="form-group">
            <Label htmlFor="message">Message</Label>
            <Textarea 
              id="message" 
              value={formData.message} 
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              required
              rows={4}
              data-testid="contact-message-input"
            />
          </div>
          <Button type="submit" className="submit-btn" disabled={loading} data-testid="contact-submit-btn">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Send Message"}
          </Button>
        </form>
      </div>
    </section>
  );
};


// Footer
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <span className="footer-title">String Creations</span>
          <p>Transforming memories into art, one thread at a time.</p>
        </div>
        <div className="footer-links">
          <a href="#how-it-works">How It Works</a>
          <a href="#gallery">Gallery</a>
          <a href="#pricing">Pricing</a>
          <a href="#contact">Contact</a>
        </div>
        <div className="footer-links">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2024 String Creations. All rights reserved.</p>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="home-page" data-testid="home-page">
      <Header />
      <HeroSection />
      <HowItWorksSection />
      <GallerySection />
      <PlacementTipsSection />
      <PricingSection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Create Order Page
const CreateOrderPage = () => {
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState("medium");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageData, setImageData] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    shipping_address: ""
  });
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  const sizes = [
    { key: "small", name: "Small", dimensions: "36x36cm", price: 169 },
    { key: "medium", name: "Medium", dimensions: "53x53cm", price: 249 },
    { key: "large", name: "Large", dimensions: "73x73cm", price: 359 }
  ];

  const selectedPrice = sizes.find(s => s.key === selectedSize)?.price || 0;
  const finalPrice = discountInfo ? discountInfo.final_price : selectedPrice;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size should be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
        setImageData(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error("Please enter a discount code");
      return;
    }
    setApplyingDiscount(true);
    try {
      const response = await axios.post(`${API}/discounts/validate`, {
        code: discountCode,
        order_total: selectedPrice
      });
      setDiscountInfo(response.data);
      toast.success(`Discount applied! You save $${response.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid discount code");
      setDiscountInfo(null);
    }
    setApplyingDiscount(false);
  };

  const handleRemoveDiscount = () => {
    setDiscountCode("");
    setDiscountInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageData) {
      toast.error("Please upload a portrait image");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        ...formData,
        size: selectedSize,
        image_data: imageData,
        discount_code: discountInfo ? discountCode : null
      });
      toast.success("Order submitted! We'll review and get back to you soon.");
      navigate(`/order-submitted/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    }
    setLoading(false);
  };

  return (
    <div className="create-order-page" data-testid="create-order-page">
      <Header />
      <div className="order-container">
        <div className="order-header">
          <h1>Create Your String Art</h1>
          <p>Upload your portrait and we'll transform it into a stunning piece of art</p>
        </div>
        <form onSubmit={handleSubmit} className="order-form">
          <div className="form-section">
            <h2>1. Upload Your Portrait</h2>
            <div className="upload-area" data-testid="upload-area">
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleImageUpload}
                id="image-upload"
                className="hidden-input"
                data-testid="image-upload-input"
              />
              <label htmlFor="image-upload" className="upload-label">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                ) : (
                  <div className="upload-placeholder">
                    <Upload size={48} />
                    <span>Click to upload your portrait</span>
                    <span className="upload-hint">JPG, PNG up to 10MB</span>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="form-section">
            <h2>2. Select Size</h2>
            <div className="free-shipping-badge-order">
              🌍 Free Worldwide Shipping • Delivery: 3-5 weeks
            </div>
            <RadioGroup value={selectedSize} onValueChange={setSelectedSize} className="size-options" data-testid="size-options">
              {sizes.map((size) => (
                <div key={size.key} className={`size-option ${selectedSize === size.key ? 'selected' : ''}`}>
                  <RadioGroupItem value={size.key} id={size.key} data-testid={`size-${size.key}`} />
                  <label htmlFor={size.key} className="size-label">
                    <span className="size-name">{size.name}</span>
                    <span className="size-dimensions">{size.dimensions}</span>
                    <span className="size-price">${size.price}</span>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="form-section">
            <h2>3. Your Details</h2>
            <div className="details-grid">
              <div className="form-group">
                <Label htmlFor="name">Full Name</Label>
                <Input 
                  id="name" 
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  required
                  data-testid="customer-email-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="phone">Phone</Label>
                <Input 
                  id="phone" 
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  required
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="form-group full-width">
                <Label htmlFor="address">Shipping Address</Label>
                <Textarea 
                  id="address" 
                  value={formData.shipping_address}
                  onChange={(e) => setFormData({...formData, shipping_address: e.target.value})}
                  required
                  rows={3}
                  data-testid="shipping-address-input"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>4. Discount Code (Optional)</h2>
            <div className="discount-input-group">
              <Input 
                placeholder="Enter discount code"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                disabled={discountInfo !== null}
                data-testid="discount-code-input"
              />
              {!discountInfo ? (
                <Button 
                  type="button"
                  onClick={handleApplyDiscount}
                  disabled={applyingDiscount || !discountCode.trim()}
                  variant="outline"
                  data-testid="apply-discount-btn"
                >
                  {applyingDiscount ? <Loader2 className="animate-spin" size={18} /> : "Apply"}
                </Button>
              ) : (
                <Button 
                  type="button"
                  onClick={handleRemoveDiscount}
                  variant="outline"
                  data-testid="remove-discount-btn"
                >
                  Remove
                </Button>
              )}
            </div>
            {discountInfo && (
              <div className="discount-applied">
                <Check size={16} />
                <span>Code "{discountCode}" applied - ${discountInfo.discount_amount} off!</span>
              </div>
            )}
          </div>

          <div className="order-summary">
            <div className="summary-row">
              <span>Selected Size:</span>
              <span>{sizes.find(s => s.key === selectedSize)?.name} ({sizes.find(s => s.key === selectedSize)?.dimensions})</span>
            </div>
            {discountInfo && (
              <>
                <div className="summary-row">
                  <span>Original Price:</span>
                  <span>${discountInfo.original_price}</span>
                </div>
                <div className="summary-row discount-row">
                  <span>Discount ({discountCode}):</span>
                  <span>-${discountInfo.discount_amount}</span>
                </div>
              </>
            )}
            <div className="summary-row total">
              <span>Total:</span>
              <span>${finalPrice}</span>
            </div>
          </div>

          <Button type="submit" className="checkout-btn" disabled={loading} data-testid="proceed-to-checkout-btn">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Proceed to Checkout"}
          </Button>
        </form>
      </div>
      <Footer />
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        toast.error("Order not found");
      }
      setLoading(false);
    };
    fetchOrder();
  }, [orderId]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/session`, {
        order_id: orderId,
        origin_url: originUrl
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-page" data-testid="loading">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="error-page" data-testid="order-not-found">
        <h1>Order Not Found</h1>
        <p>The order you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <Header />
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Complete Your Order</h1>
          <p>Review your order and proceed to payment</p>
        </div>
        <div className="checkout-content">
          <Card className="order-details-card">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="order-image">
                <img src={order.image_data} alt="Your portrait" />
              </div>
              <div className="order-info">
                <div className="info-row">
                  <span className="label">Size:</span>
                  <span className="value">{order.size_name} ({order.dimensions})</span>
                </div>
                <div className="info-row">
                  <span className="label">Name:</span>
                  <span className="value">{order.customer_name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{order.customer_email}</span>
                </div>
                <div className="info-row">
                  <span className="label">Shipping:</span>
                  <span className="value">{order.shipping_address}</span>
                </div>
                <div className="info-row total">
                  <span className="label">Total:</span>
                  <span className="value">${order.price}</span>
                </div>
              </div>
              <Button 
                onClick={handlePayment} 
                className="pay-btn" 
                disabled={processing}
                data-testid="pay-now-btn"
              >
                {processing ? (
                  <><Loader2 className="animate-spin" size={18} /> Processing...</>
                ) : (
                  <>Pay ${order.price} Now</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Order Success Page
const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Fetch order details
        const orderResponse = await axios.get(`${API}/orders/${orderId}`);
        setOrder(orderResponse.data);
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  }, [sessionId, orderId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="success-page" data-testid="order-success-page">
      <Header />
      <div className="success-container">
        {status === "checking" && (
          <div className="status-checking" data-testid="status-checking">
            <Loader2 className="animate-spin" size={64} />
            <h1>Processing Your Payment</h1>
            <p>Please wait while we confirm your payment...</p>
          </div>
        )}
        {status === "success" && (
          <div className="status-success" data-testid="status-success">
            <div className="success-icon">
              <Check size={64} />
            </div>
            <h1>Thank You for Your Order!</h1>
            <p>Your payment was successful. We've received your order and will start crafting your string art masterpiece soon.</p>
            {order && (
              <div className="order-confirmation">
                <h3>Order Details</h3>
                <p><strong>Order ID:</strong> {order.id}</p>
                <p><strong>Size:</strong> {order.size_name} ({order.dimensions})</p>
                <p><strong>Amount Paid:</strong> ${order.price}</p>
                <p><strong>Shipping to:</strong> {order.shipping_address}</p>
              </div>
            )}
            <p className="confirmation-email">A confirmation email has been sent to your email address.</p>
            <Button onClick={() => navigate("/")} className="home-btn" data-testid="back-home-btn">
              Back to Home
            </Button>
          </div>
        )}
        {status === "timeout" && (
          <div className="status-timeout" data-testid="status-timeout">
            <h1>Payment Status Unknown</h1>
            <p>We couldn't confirm your payment status. Please check your email for confirmation or contact support.</p>
            <Button onClick={() => navigate("/")} className="home-btn">Back to Home</Button>
          </div>
        )}
        {status === "expired" && (
          <div className="status-expired" data-testid="status-expired">
            <h1>Payment Session Expired</h1>
            <p>Your payment session has expired. Please try again.</p>
            <Button onClick={() => navigate("/create-order")} className="retry-btn">Create New Order</Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Order Submitted Page - shown after customer submits order
const OrderSubmittedPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  return (
    <div className="success-page" data-testid="order-submitted-page">
      <Header />
      <div className="success-container">
        <div className="status-success" data-testid="order-submitted">
          <div className="pending-icon">
            <Clock size={64} />
          </div>
          <h1>Order Submitted Successfully!</h1>
          <p>Thank you for your order. We're reviewing your portrait and will prepare a custom blueprint for you.</p>
          <div className="order-confirmation">
            <h3>What happens next?</h3>
            <ul className="next-steps">
              <li><Check size={16} /> We review your uploaded portrait</li>
              <li><Check size={16} /> We create a custom blueprint for your string art</li>
              <li><Check size={16} /> You'll receive an email to proceed with payment</li>
              <li><Check size={16} /> Once paid, we start crafting your masterpiece</li>
            </ul>
            <p className="order-id-info"><strong>Order ID:</strong> {orderId}</p>
          </div>
          <div className="spam-notice">
            <Mail size={18} />
            <span>Please check your <strong>spam/junk folder</strong> for our approval email!</span>
          </div>
          <p className="confirmation-email">Save your order ID to check the status later.</p>
          <div className="button-group">
            <Button onClick={() => navigate(`/order-status/${orderId}`)} className="home-btn" data-testid="check-status-btn">
              Check Order Status
            </Button>
            <Button onClick={() => navigate("/")} variant="outline" className="secondary-btn" data-testid="back-home-btn">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Order Status Page - customer can check their order status
const OrderStatusPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        toast.error("Order not found");
      }
      setLoading(false);
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="loading-page">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading order status...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="error-page">
        <h1>Order Not Found</h1>
        <p>The order you're looking for doesn't exist.</p>
        <Button onClick={() => navigate("/")} className="home-btn">Back to Home</Button>
      </div>
    );
  }

  const getStatusIcon = () => {
    switch (order.status) {
      case 'pending_approval': return <Clock size={64} className="status-pending" />;
      case 'approved': return <CheckCircle size={64} className="status-approved" />;
      case 'rejected': return <XCircle size={64} className="status-rejected" />;
      case 'processing': return <Loader2 size={64} className="status-processing animate-spin" />;
      default: return <Clock size={64} />;
    }
  };

  const getStatusMessage = () => {
    switch (order.status) {
      case 'pending_approval': 
        return { title: "Under Review", message: "We're reviewing your portrait and preparing a custom blueprint." };
      case 'approved': 
        return { title: "Approved!", message: "Your order has been approved. You can now proceed to payment." };
      case 'rejected': 
        return { title: "Order Declined", message: order.admin_notes || "Unfortunately, we couldn't proceed with this order. Please contact us for details." };
      case 'processing': 
        return { title: "In Production", message: "We're crafting your string art masterpiece!" };
      default: 
        return { title: "Status Unknown", message: "Please contact us for more information." };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <div className="success-page" data-testid="order-status-page">
      <Header />
      <div className="success-container">
        <div className="status-success">
          <div className="status-icon-wrapper">
            {getStatusIcon()}
          </div>
          <h1>{statusInfo.title}</h1>
          <p>{statusInfo.message}</p>
          
          {order.admin_notes && order.status === 'approved' && (
            <div className="admin-notes">
              <h4>Note from us:</h4>
              <p>{order.admin_notes}</p>
            </div>
          )}

          <div className="order-confirmation">
            <h3>Order Details</h3>
            <div className="order-image-small">
              <img src={order.image_data} alt="Your portrait" />
            </div>
            <p><strong>Size:</strong> {order.size_name} ({order.dimensions})</p>
            <p><strong>Price:</strong> ${order.price}</p>
            <p><strong>Status:</strong> {order.status.replace('_', ' ').toUpperCase()}</p>
          </div>

          {order.status === 'approved' && order.payment_status !== 'paid' && (
            <div className="payment-options">
              <h3>Complete Your Payment</h3>
              <div className="paypal-button-container">
                <PayPalScriptProvider 
                  options={{ 
                    clientId: PAYPAL_CLIENT_ID, 
                    currency: "USD",
                    intent: "capture",
                    components: "buttons",
                    enableFunding: "card",
                    disableFunding: "paylater"
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect", label: "pay" }}
                    fundingSource={undefined}
                    createOrder={async (data, actions) => {
                      try {
                        return await axios.post(`${API}/paypal/create-order-v2`, {
                          order_id: order.id,
                          amount: order.price
                        }).then(res => res.data.paypal_order_id);
                      } catch (error) {
                        toast.error("Unable to create payment. Please contact us.");
                        throw error;
                      }
                    }}
                    onApprove={async (data, actions) => {
                      try {
                        await axios.post(`${API}/paypal/capture-order`, {
                          order_id: order.id,
                          paypal_order_id: data.orderID
                        });
                        toast.success("Payment successful!");
                        setTimeout(() => {
                          window.location.reload();
                        }, 1500);
                      } catch (error) {
                        toast.error("Payment capture failed. Please contact us.");
                      }
                    }}
                    onError={(err) => {
                      toast.error("Payment error. Please try again or contact us.");
                      console.error(err);
                    }}
                  />
                </PayPalScriptProvider>
              </div>
              
              <div className="payment-help">
                <p className="payment-help-text">
                  If you have difficulties, contact <a href="mailto:fkasuli@gmail.com" className="payment-email">fkasuli@gmail.com</a>
                </p>
              </div>
            </div>
          )}

          {order.status === 'processing' && order.payment_status === 'paid' && (
            <div className="paid-notice">
              <CheckCircle size={24} />
              <span>Payment received! We're working on your string art.</span>
            </div>
          )}

          <Button onClick={() => navigate("/")} variant="outline" className="secondary-btn margin-top">
            Back to Home
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// PayPal Success Page
const PayPalSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentId = searchParams.get("paymentId");
  const payerId = searchParams.get("PayerID");
  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("processing");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const executePayment = async () => {
      if (!paymentId || !payerId || !orderId) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.post(`${API}/paypal/execute-payment`, {
          payment_id: paymentId,
          payer_id: payerId,
          order_id: orderId
        });

        if (response.data.success) {
          setStatus("success");
          // Fetch order details
          const orderResponse = await axios.get(`${API}/orders/${orderId}`);
          setOrder(orderResponse.data);
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Payment execution error:", error);
        setStatus("error");
      }
    };

    executePayment();
  }, [paymentId, payerId, orderId]);

  return (
    <div className="success-page" data-testid="paypal-success-page">
      <Header />
      <div className="success-container">
        {status === "processing" && (
          <div className="status-checking" data-testid="status-processing">
            <Loader2 className="animate-spin" size={64} />
            <h1>Processing Your Payment</h1>
            <p>Please wait while we confirm your PayPal payment...</p>
          </div>
        )}
        {status === "success" && (
          <div className="status-success" data-testid="status-success">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>Payment Successful!</h1>
            <p>Thank you for your payment. Your order has been confirmed and we'll start crafting your string art masterpiece.</p>
            {order && (
              <div className="order-confirmation">
                <h3>Order Details</h3>
                <p><strong>Order ID:</strong> {order.id}</p>
                <p><strong>Size:</strong> {order.size_name} ({order.dimensions})</p>
                <p><strong>Amount Paid:</strong> ${order.price}</p>
                <p><strong>Shipping to:</strong> {order.shipping_address}</p>
              </div>
            )}
            <p className="confirmation-email">A confirmation email has been sent to your email address.</p>
            <Button onClick={() => navigate("/")} className="home-btn" data-testid="back-home-btn">
              Back to Home
            </Button>
          </div>
        )}
        {status === "error" && (
          <div className="status-error" data-testid="status-error">
            <div className="error-icon">
              <XCircle size={64} />
            </div>
            <h1>Payment Error</h1>
            <p>There was an issue processing your PayPal payment. Please try again or contact support.</p>
            <div className="button-group">
              <Button onClick={() => navigate(`/order-status/${orderId}`)} className="retry-btn">
                Back to Order
              </Button>
              <Button onClick={() => navigate("/")} variant="outline" className="home-btn">
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Check Order Page - allows customers to check order status by ID
const CheckOrderPage = () => {
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) {
      toast.error("Please enter an order ID");
      return;
    }
    setLoading(true);
    try {
      // Check if order exists
      await axios.get(`${API}/orders/${orderId.trim()}`);
      navigate(`/order-status/${orderId.trim()}`);
    } catch (error) {
      toast.error("Order not found. Please check your order ID.");
    }
    setLoading(false);
  };

  return (
    <div className="check-order-page" data-testid="check-order-page">
      <Header />
      <div className="check-order-container">
        <div className="check-order-header">
          <h1>Check Your Order Status</h1>
          <p>Enter your order ID to check the current status of your string art commission</p>
        </div>
        <Card className="check-order-card">
          <CardHeader>
            <CardTitle>Order Lookup</CardTitle>
            <CardDescription>Your order ID was provided when you submitted your order</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="check-order-form">
              <div className="form-group">
                <Label htmlFor="orderId">Order ID</Label>
                <Input 
                  id="orderId" 
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="Enter your order ID"
                  required
                  data-testid="order-id-input"
                />
              </div>
              <Button type="submit" className="submit-btn" disabled={loading} data-testid="check-order-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Check Order Status"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};


// About Us Page
const AboutUsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const desktopStyles = {
    heroSection: { display: 'flex', flexDirection: 'row', gap: '3rem', alignItems: 'flex-start' },
    videoColumn: { width: '300px', flexShrink: 0 }
  };

  const mobileStyles = {
    heroSection: { display: 'flex', flexDirection: 'column', gap: '2rem' },
    videoColumn: { width: '100%' }
  };

  const styles = isMobile ? mobileStyles : desktopStyles;

  return (
    <div className="about-us-page">
      <Header />
      <div className="about-container">
        <div className="about-header">
          <h1>About String Creations</h1>
          <p className="about-subtitle">Where Art Meets Craftsmanship</p>
        </div>

        <div className="about-content">
          <div className="about-hero-section" style={styles.heroSection}>
            <div className="about-video-column" style={styles.videoColumn}>
              <div className="video-wrapper">
                <video 
                  controls 
                  className="about-video"
                  preload="metadata"
                >
                  <source src="https://customer-assets.emergentagent.com/job_artful-strings-1/artifacts/vsf8bjvw_v09044g40000d4bmuovog65r8b50b3og.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <div className="about-text-column">
              <div className="about-story">
                <h2>Our Story</h2>
                <p>
                  String Creations was born from a passion for transforming cherished memories into stunning works of art. 
                  Each piece we create tells a unique story, woven together with precision, patience, and love.
                </p>
                <p>
                  Our string art technique combines traditional craftsmanship with modern design, creating portraits 
                  that capture not just the likeness, but the essence of your loved ones. Every nail is carefully 
                  placed, every thread intentionally wound to bring your vision to life.
                </p>
              </div>

              <div className="about-process">
                <h2>Our Process</h2>
                <div className="process-grid">
                  <div className="process-item">
                    <h3><strong>1. Your Photo</strong></h3>
                    <p>You share your special moment or beloved portrait with us.</p>
                  </div>
                  <div className="process-item">
                    <h3><strong>2. Design Review</strong></h3>
                    <p>We carefully review and prepare your design for perfection.</p>
                  </div>
                  <div className="process-item">
                    <h3><strong>3. Handcrafted Art</strong></h3>
                    <p>Each piece is meticulously crafted by hand with premium materials.</p>
                  </div>
                  <div className="process-item">
                    <h3><strong>4. Delivered Worldwide</strong></h3>
                    <p>Your unique masterpiece arrives ready to display with free shipping.</p>
                  </div>
                </div>
              </div>

              <div className="about-commitment">
                <h2>Our Commitment</h2>
                <div className="commitment-grid">
                  <div className="commitment-item">
                    <h3>🎨 Quality Craftsmanship</h3>
                    <p>Every piece is handmade with attention to detail and artistic precision.</p>
                  </div>
                  <div className="commitment-item">
                    <h3>💝 Personal Touch</h3>
                    <p>We treat each order as a unique work of art, not just another product.</p>
                  </div>
                  <div className="commitment-item">
                    <h3>🌍 Global Delivery</h3>
                    <p>Free worldwide shipping ensures your art reaches you wherever you are.</p>
                  </div>
                  <div className="commitment-item">
                    <h3>✨ Lasting Memories</h3>
                    <p>Creating pieces that become treasured family heirlooms for generations.</p>
                  </div>
                </div>
              </div>

              <div className="about-cta">
                <h2>Ready to Create Your Masterpiece?</h2>
                <p>Turn your favorite photo into a stunning piece of string art today.</p>
                <Button onClick={() => navigate('/create-order')} size="lg" className="cta-btn">
                  Start Your Order
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};




// Analytics Page Component
const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="analytics-loading">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>📊 Analytics Dashboard</h1>
        <Button onClick={() => navigate("/admin")} variant="outline">
          Back to Orders
        </Button>
      </div>

      <div className="analytics-grid">
        {/* Key Metrics */}
        <Card className="analytics-card metric-card">
          <CardHeader>
            <CardTitle>Total Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="metric-value">{analytics?.total_orders || 0}</div>
          </CardContent>
        </Card>

        <Card className="analytics-card metric-card">
          <CardHeader>
            <CardTitle>Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="metric-value">${analytics?.total_revenue || 0}</div>
            <div className="metric-breakdown">
              <span className="paid">Paid: ${analytics?.paid_revenue || 0}</span>
              <span className="pending">Pending: ${analytics?.pending_revenue || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="analytics-card metric-card">
          <CardHeader>
            <CardTitle>Conversion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="metric-value">{analytics?.conversion_rate || 0}%</div>
            <p className="metric-desc">Orders approved or paid</p>
          </CardContent>
        </Card>

        {/* Orders by Status */}
        <Card className="analytics-card">
          <CardHeader>
            <CardTitle>Orders by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="status-breakdown">
              {analytics?.orders_by_status && Object.entries(analytics.orders_by_status).map(([status, count]) => (
                <div key={status} className="status-item">
                  <span className={`status-label status-${status}`}>
                    {status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="status-count">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Orders by Size */}
        <Card className="analytics-card">
          <CardHeader>
            <CardTitle>Orders by Size</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="size-breakdown">
              {analytics?.orders_by_size && Object.entries(analytics.orders_by_size).map(([size, count]) => (
                <div key={size} className="size-item">
                  <span className="size-label">{size.toUpperCase()}</span>
                  <div className="size-bar">
                    <div 
                      className="size-bar-fill" 
                      style={{ width: `${(count / analytics.total_orders) * 100}%` }}
                    ></div>
                  </div>
                  <span className="size-count">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Google Analytics Info */}
        <Card className="analytics-card ga-card">
          <CardHeader>
            <CardTitle>🌍 Website Traffic Analytics</CardTitle>
            <CardDescription>Visitor demographics & behavior</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="ga-info">
              For detailed website analytics including:
            </p>
            <ul className="ga-features">
              <li>✅ Real-time visitor tracking</li>
              <li>✅ Country & city locations</li>
              <li>✅ Demographics (age & gender)</li>
              <li>✅ Page views & clicks</li>
              <li>✅ User behavior flow</li>
            </ul>
            <Button 
              className="ga-button"
              onClick={() => window.open('https://analytics.google.com/analytics/web/#/p' + analytics?.ga_property_id || '', '_blank')}
            >
              View Google Analytics Dashboard →
            </Button>
            <p className="ga-note">
              📝 Note: Demographics data may take 24-48 hours to appear after first visitor
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [filter, setFilter] = useState("pending_approval");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      toast.success("Login successful");
      fetchOrders();
    } catch (error) {
      toast.error("Invalid password");
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        params: filter ? { status: filter } : {}
      });
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchOrders();
    }
  }, [isLoggedIn, filter]);

  const handleApprove = async (orderId) => {
    try {
      await axios.post(`${API}/admin/approve`, { 
        order_id: orderId, 
        admin_notes: adminNotes 
      });
      toast.success("Order approved! Customer can now pay.");
      setSelectedOrder(null);
      setAdminNotes("");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to approve order");
    }
  };

  const handleReject = async (orderId) => {
    try {
      await axios.post(`${API}/admin/reject`, { 
        order_id: orderId, 
        admin_notes: adminNotes 
      });
      toast.success("Order rejected.");
      setSelectedOrder(null);
      setAdminNotes("");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to reject order");
    }
  };

  const handleConfirmPayment = async (orderId) => {
    try {
      await axios.post(`${API}/admin/confirm-payment`, { 
        order_id: orderId 
      });
      toast.success("Payment confirmed! Order is now in processing.");
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to confirm payment");
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-page" data-testid="admin-login">
        <div className="admin-login-container">
          <Card className="admin-login-card">
            <CardHeader>
              <CardTitle><Lock size={24} /> Admin Login</CardTitle>
              <CardDescription>Enter password to access admin panel</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password-input"
                  />
                </div>
                <Button type="submit" className="submit-btn" data-testid="admin-login-btn">
                  Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-panel">
      <div className="admin-header">
        <h1>Order Management</h1>
        <p style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <a href="/admin/analytics" style={{ color: '#10B981', fontSize: '1.2rem', fontWeight: '600', textDecoration: 'underline' }}>
            📊 View Analytics Dashboard
          </a>
        </p>
        <div className="admin-filters">
          <Button 
            variant={filter === "pending_approval" ? "default" : "outline"}
            onClick={() => setFilter("pending_approval")}
          >
            Pending Review
          </Button>
          <Button 
            variant={filter === "approved" ? "default" : "outline"}
            onClick={() => setFilter("approved")}
          >
            Approved
          </Button>
          <Button 
            variant={filter === "" ? "default" : "outline"}
            onClick={() => setFilter("")}
          >
            All Orders
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">
          <Loader2 className="animate-spin" size={32} />
          <p>Loading orders...</p>
        </div>
      ) : (
        <div className="admin-orders-grid">
          {orders.length === 0 ? (
            <p className="no-orders">No orders found</p>
          ) : (
            orders.map((order) => (
              <Card key={order.id} className="admin-order-card" data-testid={`order-${order.id}`}>
                <CardContent>
                  <div className="order-preview-image">
                    <img src={order.image_data} alt="Customer portrait" />
                  </div>
                  <div className="order-details">
                    <h3>{order.customer_name}</h3>
                    <p className="order-email">{order.customer_email}</p>
                    <p className="order-phone">{order.customer_phone}</p>
                    <p className="order-size">{order.size_name} ({order.dimensions})</p>
                    <p className="order-price">${order.price}</p>
                    <p className="order-address">{order.shipping_address}</p>
                    <span className={`order-status status-${order.status}`}>
                      {order.status.replace('_', ' ')}
                    </span>
                  </div>
                  
                  {order.status === 'pending_approval' && (
                    <div className="order-actions">
                      <Button 
                        onClick={() => setSelectedOrder(order)}
                        className="view-btn"
                      >
                        <Eye size={16} /> Review
                      </Button>
                    </div>
                  )}
                  
                  {order.status === 'approved' && order.payment_status !== 'paid' && (
                    <div className="order-actions">
                      <Button 
                        onClick={() => handleConfirmPayment(order.id)}
                        className="confirm-payment-btn"
                      >
                        <CheckCircle size={16} /> Confirm Payment
                      </Button>
                    </div>
                  )}
                  
                  {order.payment_status === 'paid' && (
                    <div className="paid-badge">
                      <CheckCircle size={16} /> Paid
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Review Order</h2>
            <div className="modal-image">
              <img src={selectedOrder.image_data} alt="Customer portrait" />
            </div>
            <div className="modal-details">
              <p><strong>Customer:</strong> {selectedOrder.customer_name}</p>
              <p><strong>Email:</strong> {selectedOrder.customer_email}</p>
              <p><strong>Phone:</strong> {selectedOrder.customer_phone}</p>
              <p><strong>Size:</strong> {selectedOrder.size_name} ({selectedOrder.dimensions})</p>
              <p><strong>Price:</strong> ${selectedOrder.price}</p>
              <p><strong>Shipping:</strong> {selectedOrder.shipping_address}</p>
            </div>
            <div className="form-group">
              <Label>Notes for customer (optional)</Label>
              <Textarea 
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add any notes about the blueprint or order..."
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <Button onClick={() => handleApprove(selectedOrder.id)} className="approve-btn">
                <CheckCircle size={16} /> Approve
              </Button>
              <Button onClick={() => handleReject(selectedOrder.id)} variant="destructive" className="reject-btn">
                <XCircle size={16} /> Reject
              </Button>
              <Button onClick={() => setSelectedOrder(null)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <Header />
      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: November 30, 2024</p>

        <section>
          <h2>1. Information We Collect</h2>
          <p>When you place an order with String Creations, we collect the following information:</p>
          <ul>
            <li><strong>Personal Information:</strong> Name, email address, phone number, and shipping address</li>
            <li><strong>Payment Information:</strong> Payment details are processed securely through PayPal</li>
            <li><strong>Order Information:</strong> Photos you upload for your custom string art, size preferences, and order history</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our website through Google Analytics</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Process and fulfill your custom string art orders</li>
            <li>Communicate with you about your order status and shipping updates</li>
            <li>Send order confirmation and approval notifications via email</li>
            <li>Improve our website and services</li>
            <li>Respond to your inquiries and provide customer support</li>
          </ul>
        </section>

        <section>
          <h2>3. Information Sharing</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information with:</p>
          <ul>
            <li><strong>Payment Processors:</strong> PayPal, to process your payments securely</li>
            <li><strong>Email Service:</strong> SendGrid, to send you order-related notifications</li>
            <li><strong>Analytics:</strong> Google Analytics, to understand website usage (anonymized data)</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Security</h2>
          <p>We implement appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. Your payment information is processed through secure payment gateways and we do not store credit card details on our servers.</p>
        </section>

        <section>
          <h2>5. Your Images</h2>
          <p>Photos you upload for custom string art creation are:</p>
          <ul>
            <li>Used solely for creating your custom artwork</li>
            <li>Stored securely on our servers</li>
            <li>Not shared with third parties except as necessary to fulfill your order</li>
            <li>Retained for order fulfillment and customer service purposes</li>
          </ul>
        </section>

        <section>
          <h2>6. Cookies and Tracking</h2>
          <p>Our website uses cookies and similar tracking technologies to enhance your browsing experience. We use Google Analytics to understand how visitors interact with our site. You can control cookies through your browser settings.</p>
        </section>

        <section>
          <h2>7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Opt-out of marketing communications</li>
          </ul>
        </section>

        <section>
          <h2>8. Contact Us</h2>
          <p>If you have any questions about this Privacy Policy or how we handle your data, please contact us at:</p>
          <p><strong>Email:</strong> fkasuli@gmail.com</p>
        </section>

        <div className="legal-actions">
          <Button onClick={() => navigate("/")} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Terms of Service Page
const TermsOfServicePage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="legal-page" data-testid="terms-page">
      <Header />
      <div className="legal-content">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: November 30, 2024</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>By accessing and using String Creations' website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
        </section>

        <section>
          <h2>2. Service Description</h2>
          <p>String Creations provides custom handcrafted string art based on photos you provide. Each piece is uniquely created by our skilled artisans. We offer three sizes:</p>
          <ul>
            <li>Small: 36x36cm - $169</li>
            <li>Medium: 53x53cm - $249</li>
            <li>Large: 73x73cm - $359</li>
          </ul>
          <p>All prices include free worldwide shipping.</p>
        </section>

        <section>
          <h2>3. Order Process</h2>
          <h3>3.1 Order Submission</h3>
          <p>When you place an order, you must provide a clear, high-quality photo and complete shipping information. Submitting an order does not guarantee acceptance.</p>
          
          <h3>3.2 Order Approval</h3>
          <p>All orders are subject to admin approval. We will review your photo to ensure it is suitable for string art creation. You will receive an email notification once your order is approved or if there are any issues.</p>
          
          <h3>3.3 Payment</h3>
          <p>Payment is required only after your order has been approved. We accept payments through PayPal, including credit and debit cards. Payment must be completed before we begin creating your artwork.</p>
        </section>

        <section>
          <h2>4. Production and Delivery</h2>
          <h3>4.1 Production Time</h3>
          <p>Custom string art creation typically takes 3-5 weeks after payment confirmation. This timeframe may vary based on order complexity and current demand.</p>
          
          <h3>4.2 Shipping</h3>
          <p>We offer free worldwide shipping. Delivery times vary by location but typically take 1-2 weeks after production is complete. You will receive tracking information once your order ships.</p>
        </section>

        <section>
          <h2>5. Image Requirements and Rights</h2>
          <h3>5.1 Your Responsibilities</h3>
          <p>You must:</p>
          <ul>
            <li>Provide clear, high-resolution images</li>
            <li>Own the rights to the images you submit or have permission to use them</li>
            <li>Ensure images do not contain inappropriate or offensive content</li>
          </ul>
          
          <h3>5.2 Image Usage</h3>
          <p>By submitting images, you grant String Creations the right to use them solely for creating your custom artwork. We may use completed artwork images for promotional purposes unless you request otherwise.</p>
        </section>

        <section>
          <h2>6. Refunds and Cancellations</h2>
          <h3>6.1 Cancellation Policy</h3>
          <p>You may cancel your order before it is approved at no charge. Once production has begun (after payment), orders cannot be cancelled.</p>
          
          <h3>6.2 Refunds</h3>
          <p>Refunds are provided if:</p>
          <ul>
            <li>We are unable to complete your order</li>
            <li>Your artwork arrives damaged (photo evidence required within 48 hours of delivery)</li>
            <li>There is a significant quality issue with the craftsmanship</li>
          </ul>
          <p>Due to the custom nature of our products, we do not offer refunds based on personal preference once production has begun.</p>
        </section>

        <section>
          <h2>7. Quality and Variations</h2>
          <p>Each piece is handcrafted and may have slight variations from the preview. These variations are inherent to the handmade nature of string art and add to the unique character of your piece.</p>
        </section>

        <section>
          <h2>8. Intellectual Property</h2>
          <p>All content on the String Creations website, including designs, text, graphics, and logos, is the property of String Creations and protected by copyright laws.</p>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          <p>String Creations is not liable for any indirect, incidental, or consequential damages arising from the use of our services or products. Our total liability is limited to the amount paid for your order.</p>
        </section>

        <section>
          <h2>10. Changes to Terms</h2>
          <p>We reserve the right to modify these Terms of Service at any time. Changes will be posted on this page with an updated revision date. Continued use of our services after changes constitutes acceptance of the modified terms.</p>
        </section>

        <section>
          <h2>11. Contact Information</h2>
          <p>For questions about these Terms of Service, please contact us at:</p>
          <p><strong>Email:</strong> fkasuli@gmail.com</p>
        </section>

        <div className="legal-actions">
          <Button onClick={() => navigate("/")} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App" data-testid="app">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/create-order" element={<CreateOrderPage />} />
          <Route path="/order-submitted/:orderId" element={<OrderSubmittedPage />} />
          <Route path="/order-status/:orderId" element={<OrderStatusPage />} />
          <Route path="/checkout/:orderId" element={<CheckoutPage />} />
          <Route path="/order-success" element={<OrderSuccessPage />} />
          <Route path="/paypal-success" element={<PayPalSuccessPage />} />
          <Route path="/check-order" element={<CheckOrderPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/analytics" element={<AnalyticsPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
